import React from 'react'
import { useQuery } from '@apollo/client'
import { Form } from 'antd'
import dayjs from 'dayjs'
import 'dayjs/locale/fr.js'
import { DatePicker } from 'antd'
import { GREFFES_QUERY } from 'graphql/Greffes'
import { GreffeType } from 'types'
import { Section } from './components'
import { Field } from './styles'
import useAutoSave from './useAutoSave'

const NOTICE_PROVIDERS = [null, 'les_echos'] as const
const DEPOSIT_PROVIDERS = [null, 'guichet_unique'] as const

export type LegalFormalityValues = {
  domiciliationId: string
  depositProvider: string
  noticeProvider: string
  signatureDate: string
  signatureCity: string
  capitalDepositDate: string
  capitalDepositCity: string
  finalLegalAuthorityId: string
}

export const LEGAL_FORMALITY_EMPTY: LegalFormalityValues = {
  domiciliationId: '',
  depositProvider: '',
  noticeProvider: '',
  signatureDate: '',
  signatureCity: '',
  capitalDepositDate: '',
  capitalDepositCity: '',
  finalLegalAuthorityId: '',
}

type Props = {
  initialValues: LegalFormalityValues
  legalFormalityId: number
}

const LegalFormalitySection = ({ initialValues, legalFormalityId }: Props) => {
  const [form] = Form.useForm<LegalFormalityValues>()
  const { status, onValuesChange, textFieldProps } = useAutoSave({
    form,
    legalFormalityId,
    initialValues,
    textFields: ['domiciliationId', 'signatureCity', 'capitalDepositCity'],
  })
  const { data: greffesData } = useQuery(GREFFES_QUERY)

  return (
    <Section title="Formalité juridique" defaultOpen status={status}>
      <Form
        form={form}
        onValuesChange={onValuesChange}
        layout="vertical"
        style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
      >
        <Field>
          <label>Domiciliation ID</label>
          <Form.Item name="domiciliationId" noStyle>
            <input placeholder="—" {...textFieldProps('domiciliationId')} />
          </Form.Item>
        </Field>
        <Field>
          <label>Prestataire dépôt</label>
          <Form.Item name="depositProvider" noStyle>
            <select>
              {DEPOSIT_PROVIDERS.map(p => (
                <option key={p ?? 'null'} value={p ?? ''}>
                  {p ?? '— manuel —'}
                </option>
              ))}
            </select>
          </Form.Item>
        </Field>
        <Field>
          <label>Prestataire annonce légale</label>
          <Form.Item name="noticeProvider" noStyle>
            <select>
              {NOTICE_PROVIDERS.map(p => (
                <option key={p ?? 'null'} value={p ?? ''}>
                  {p ?? '— manuel —'}
                </option>
              ))}
            </select>
          </Form.Item>
        </Field>
        <Field>
          <label>Greffe final</label>
          <Form.Item name="finalLegalAuthorityId" noStyle>
            <select style={{ minWidth: 300 }}>
              <option value="">— aucun —</option>
              {[...(greffesData?.greffes ?? [])]
                .sort((a: GreffeType, b: GreffeType) =>
                  a.name.localeCompare(b.name)
                )
                .map((greffe: GreffeType) => (
                  <option key={greffe.id} value={greffe.id}>
                    {greffe.name}
                  </option>
                ))}
            </select>
          </Form.Item>
        </Field>
        <Field>
          <label>Date de signature</label>
          <Form.Item
            name="signatureDate"
            noStyle
            getValueFromEvent={d => (d ? d.format('YYYY-MM-DD') : '')}
            getValueProps={v => ({ value: v ? dayjs(v) : null })}
          >
            <DatePicker format="DD/MM/YYYY" />
          </Form.Item>
        </Field>
        <Field>
          <label>Lieu de signature</label>
          <Form.Item name="signatureCity" noStyle>
            <input placeholder="—" {...textFieldProps('signatureCity')} />
          </Form.Item>
        </Field>
        <Field>
          <label>Date dépôt capital</label>
          <Form.Item
            name="capitalDepositDate"
            noStyle
            getValueFromEvent={d => (d ? d.format('YYYY-MM-DD') : '')}
            getValueProps={v => ({ value: v ? dayjs(v) : null })}
          >
            <DatePicker format="DD/MM/YYYY" />
          </Form.Item>
        </Field>
        <Field>
          <label>Ville dépôt capital</label>
          <Form.Item name="capitalDepositCity" noStyle>
            <input placeholder="—" {...textFieldProps('capitalDepositCity')} />
          </Form.Item>
        </Field>
      </Form>
    </Section>
  )
}

export default LegalFormalitySection
